import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  techdocsStorageApiRef,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';

import { computeTierSwitch, currentTierFromPath } from './versioning';
import { fetchVersionsManifest } from './versionsCache';
import { fetchTierConfig } from './tiersCache';
import { ResolvedTierConfig, TierDefinition } from './tiers/types';

/**
 * Tier navigation state for the reader-header addons (Story 6.3, Task 1).
 * Runs *inside* the TechDocs reader context (`useTechDocsReaderPage()`); the
 * route-level redirect wrapper cannot use this hook — it shares the pure
 * helpers and session caches instead (see Dev Notes on the two contexts).
 */
export type TierNavigation = {
  /** True until the tier config lookup settles. */
  loading: boolean;
  /** Resolved tiers in declaration order; empty when unresolved/fail-closed. */
  tiers: TierDefinition[];
  /** The default tier's id, or null when unresolved. */
  defaultTier: string | null;
  /**
   * The tier segment carried by the current URL (validated by membership),
   * or null when the path has none — the manifest scope `VersionPicker`
   * needs (AC 6: tier manifest when the URL carries a tier, root otherwise).
   */
  urlTier: string | null;
  /** The effective current tier id: URL tier, else the default tier. */
  currentTier: string | null;
  /** Navigate to a tier, preserving place per AC 3. */
  switchTo: (tierId: string) => Promise<void>;
};

export function useTierNavigation(): TierNavigation {
  const { entityRef } = useTechDocsReaderPage();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const storageApi = useApi(techdocsStorageApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [state, setState] = useState<{
    loading: boolean;
    config: ResolvedTierConfig | null;
  }>({ loading: true, config: null });

  const entityKey =
    `${entityRef.kind}:${entityRef.namespace}:${entityRef.name}`.toLowerCase();

  useEffect(() => {
    let active = true;
    // Reset to the fail-closed state before the next entity's lookup resolves
    // so a stale tier list never drives navigation for the wrong entity.
    setState({ loading: true, config: null });
    fetchTierConfig(catalogApi, entityRef).then(config => {
      if (active) {
        setState({ loading: false, config });
      }
    });
    return () => {
      active = false;
    };
    // entityKey captures the only identity that changes the fetch; catalogApi is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey, catalogApi]);

  const { config } = state;
  const tiers = config?.tiers ?? [];
  const tierIds = tiers.map(t => t.id);
  const defaultTier = config?.defaultTier ?? null;
  const urlTier = currentTierFromPath(pathname, tierIds, entityRef);
  const currentTier = urlTier ?? defaultTier;

  const switchTo = useCallback(
    async (tierId: string) => {
      if (!config || tierId === currentTier || !tierIds.includes(tierId)) {
        return;
      }
      // Two manifests, both session-cached: the current scope's list locates
      // the version segment in the path; the destination tier's list decides
      // whether the current version survives the switch (per-tier manifests
      // diverge — ADR T1/T2). A missing destination manifest (declared tier
      // not yet published) degrades to `latest`, which 404s in the reader —
      // acceptable and diagnosable per the ADR (T5).
      const [currentManifest, targetManifest] = await Promise.all([
        fetchVersionsManifest(
          storageApi,
          fetchApi,
          entityRef,
          urlTier ?? undefined,
        ),
        fetchVersionsManifest(storageApi, fetchApi, entityRef, tierId),
      ]);
      const switchPlan = computeTierSwitch(
        pathname,
        tierIds,
        currentManifest?.versions ?? [],
        tierId,
        targetManifest?.versions ?? [],
        entityRef,
      );
      let destination = switchPlan.path;
      if (switchPlan.hasSubPath) {
        try {
          // AC 3: only preserve the sub-path if it exists in the destination
          // tier; otherwise fall back to that tier's root index. getEntityDocs
          // appends `/index.html` itself, so pass the directory path.
          await storageApi.getEntityDocs(entityRef, switchPlan.docPath);
        } catch {
          destination = switchPlan.rootPath;
        }
      }
      // Preserve any query string / anchor so switching tiers doesn't drop
      // reader query params or a #section anchor (AC 3).
      navigate(`${destination}${search}${hash}`);
    },
    // entityRef is identified by entityKey; the APIs are stable singletons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, currentTier, urlTier, pathname, search, hash, entityKey],
  );

  return {
    loading: state.loading,
    tiers,
    defaultTier,
    urlTier,
    currentTier,
    switchTo,
  };
}
